import {
  EyeIcon,
  FileIcon,
  PencilIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@krea/shared/design-system/Button";
import {
  type FileUploadActions,
  type FileWithPreview,
  isImage,
} from "@krea/shared/hooks/useFileUpload";
import { cn } from "@krea/shared/utils";

import { CropModal } from "./CropModal";

export type PreviewStyle = "grid" | "list";

interface IFilePreviewProps {
  actions: FileUploadActions;
  isDragging: boolean;
  files: FileWithPreview[];
  className?: string;
  multiple: boolean;
}

export const ListFilePreview = ({
  actions,
  isDragging,
  files,
  className,
  multiple,
}: IFilePreviewProps) => {
  const {
    openFileDialog,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    getInputProps,
    removeFile,
  } = actions;
  const hasFiles = files.length > 0;
  const isDisabled = files.length > 0 && !multiple;

  return (
    <div className="relative">
      {/*  Drop area */}
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onClick={isDisabled ? undefined : openFileDialog}
        onDragEnter={isDisabled ? undefined : handleDragEnter}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDrop={isDisabled ? undefined : handleDrop}
        onKeyDown={
          isDisabled
            ? undefined
            : (e) => {
                if (e.key === "Enter") {
                  openFileDialog();
                }
              }
        }
        data-dragging={isDragging || undefined}
        data-disabled={isDisabled || undefined}
        className={cn(
          "relative flex min-h-6 flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed border-primary p-4 transition-colors hover:bg-primary-150 has-[img]:border-none has-[input:focus]:border-primary has-[input:focus]:ring has-[input:focus]:ring-primary/50 data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-gray-300 data-[dragging=true]:bg-primary/50 data-[disabled=true]:opacity-50",
          className,
        )}
      >
        <input
          {...getInputProps()}
          className="sr-only"
          aria-label="Upload file"
          disabled={isDisabled}
        />

        <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
          <Button variant="secondary" disabled={isDisabled}>
            <UploadIcon className="mr-1 size-4" aria-hidden="true" />
            {"Select image"}
          </Button>
        </div>
      </div>
      {hasFiles && (
        <div className="mt-2 flex flex-col gap-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between gap-2 rounded-md bg-primary-150 p-2"
            >
              <div className="flex items-center text-sm font-medium">
                <FileIcon className="mr-1 size-4" />
                <span>{file.file.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <EyeIcon
                  className="mr-1 size-4 cursor-pointer text-primary"
                  onClick={() => window.open(file.preview, "_blank")}
                />

                <Trash2Icon
                  className="size-4 cursor-pointer text-red"
                  onClick={() => removeFile(file.id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ImageFilePreview = ({
  fileWithImage,
  addFiles,
  removeFile,
}: {
  fileWithImage: FileWithPreview;
  addFiles: (files: File[], isEditing?: boolean) => void;
  removeFile: (id: string) => void;
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  return (
    <div className="relative aspect-square rounded-md">
      {isCropModalOpen && (
        <CropModal
          file={fileWithImage}
          isOpen={isCropModalOpen}
          addFile={(file) => {
            addFiles([file], true);
          }}
          removeFile={(id) => removeFile(id)}
          setIsOpen={setIsCropModalOpen}
        />
      )}

      <img
        src={fileWithImage.preview}
        alt={fileWithImage.file.name}
        className="size-full rounded-[inherit] object-cover"
      />
      <div className="absolute right-2 top-2 flex gap-1">
        <Button
          size="icon"
          className="size-6 rounded-full shadow-none"
          onClick={(e) => {
            e.stopPropagation();
            setIsCropModalOpen(true);
          }}
        >
          <PencilIcon className="size-3.5" strokeWidth={2.5} />
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            removeFile(fileWithImage.id);
          }}
          size="icon"
          className="size-6 rounded-full shadow-none"
          aria-label="Remove image"
        >
          <XIcon className="size-3.5" strokeWidth={2.5} />
        </Button>
      </div>
    </div>
  );
};

const OtherFilePreview = ({
  file,

  removeFile,
}: {
  file: FileWithPreview;
  addFiles: (files: File[]) => void;
  removeFile: (id: string) => void;
}) => {
  return (
    <div className="relative flex flex-col items-center justify-center rounded-md bg-primary-150 p-2">
      <FileIcon className="size-full rounded-[inherit] object-cover text-primary" />
      <span className="text-xs font-medium">{file.file.name}</span>
      <Button
        size="icon"
        className="absolute right-2 top-2 size-6 rounded-full shadow-none"
        onClick={(e) => {
          e.stopPropagation();
          removeFile(file.id);
        }}
      >
        <XIcon className="size-3.5" strokeWidth={2.5} />
      </Button>
    </div>
  );
};

export const GridFilePreview = ({
  actions,

  isDragging,
  files,
  className,
  multiple,
}: IFilePreviewProps) => {
  const {
    openFileDialog,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    getInputProps,
    removeFile,
    addFiles,
  } = actions;

  const isDisabled = files.length > 0 && !multiple;

  return (
    <div className="relative">
      {/*  Drop area */}
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onClick={isDisabled ? () => {} : openFileDialog}
        onDragEnter={isDisabled ? undefined : handleDragEnter}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDrop={isDisabled ? undefined : handleDrop}
        onKeyDown={
          isDisabled
            ? undefined
            : (e) => {
                if (e.key === "Enter") {
                  openFileDialog();
                }
              }
        }
        data-dragging={isDragging || undefined}
        className={cn(
          "relative flex min-h-6 flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed border-primary p-4 transition-colors hover:bg-primary-150 has-[input:focus]:border-primary has-[input:focus]:ring has-[input:focus]:ring-primary/50 data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-gray-300 data-[dragging=true]:bg-primary/50 data-[disabled=true]:opacity-50",
          {
            "bg-transparent p-0 hover:bg-transparent has-[img]:border-none":
              files.length === 1,
          },
          className,
        )}
      >
        <input
          {...getInputProps()}
          className="sr-only"
          aria-label="Upload file"
          disabled={isDisabled}
        />

        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
            <Button variant="secondary" disabled={isDisabled}>
              <UploadIcon className="mr-1 size-4" aria-hidden="true" />
              {"Select image"}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {multiple && (
              <div className="flex items-center justify-between gap-2">
                <span>Uploading {files.length} files</span>
                <Button variant="secondary" disabled={isDisabled} size="sm">
                  <UploadIcon
                    className="mr-1 size-4"
                    aria-hidden="true"
                  />
                  {"Select image"}
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {files.map((file) => (
                <div
                  key={file.id}
                  className={cn("relative aspect-square rounded-md", {
                    "col-span-3": !multiple,
                  })}
                >
                  {isImage(file) ? (
                    <ImageFilePreview
                      fileWithImage={file}
                      addFiles={addFiles}
                      removeFile={removeFile}
                    />
                  ) : (
                    <OtherFilePreview
                      file={file}
                      addFiles={addFiles}
                      removeFile={removeFile}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
